/**
 * Hardens a `?next=` query parameter before we redirect to it.
 *
 * Threat model: an attacker sends someone a link like
 * `/login?next=//evil.com/phish`. The router's "on successful login,
 * push(next)" step would otherwise treat `//evil.com` as a
 * protocol-relative URL and navigate the browser off-site, landing
 * the user on a look-alike login page where their freshly-typed
 * credentials can be harvested.
 *
 * We accept only **strings that are same-origin path references**:
 *   - must start with a single `/`
 *   - must NOT start with `//` (protocol-relative)
 *   - must NOT start with `/\\` (Windows-style UNC escape some
 *     browsers interpret as absolute)
 *   - must NOT be an absolute URL (`http://`, `https://`, etc.) —
 *     covered transitively by the "must start with `/`" rule but
 *     worth calling out.
 *
 * Anything else falls back to `/` (the dashboard home).
 *
 * Returns a guaranteed-safe path. Never throws.
 */
export function safeNextPath(value: unknown, fallback = '/'): string {
  if (typeof value !== 'string' || value.length === 0) return fallback;
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//')) return fallback;
  if (value.startsWith('/\\')) return fallback;
  return value;
}
